// Matches the $lg breakpoint the horizontal sheet layout starts at.
const HORIZONTAL_LAYOUT_QUERY = "(min-width: 990px)";

const FIXED_BUTTON_HIDDEN_CLASS = "facets-button-show--hidden";

// The band the fixed opener occupies at the bottom of the viewport: its own height
// (~50px) plus its largest bottom offset (36px from $md), rounded up so the fade is
// over before the footer actually reaches the pill.
const FOOTER_OVERLAP_OFFSET = 120;
const FOOTER_OVERLAP_MARGIN = `0px 0px -${FOOTER_OVERLAP_OFFSET}px 0px`;

// Backstop for the sheet's opening transition, a touch over --duration-default.
const FOCUS_TRAP_FALLBACK_MS = 400;

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.closeFacets = this.closeFacets.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    this.facetButtons = document.querySelectorAll(".facets-button-show");
    this.productGrid = document.getElementById("ProductGridContainer");
    this.facetForm = this.querySelector("form");
    this.facetsWrapper = this.querySelector(".facets__wrapper");
    this.closeButton = this.querySelector(".facets__close");
    this.facetsOverlay = this.querySelector(".facets__overlay");
    this.facetSubmitButton = this.querySelector(".facets__submit");

    this.facetForm?.addEventListener("input", (event) => {
      // The response already on its way re-renders this very form from older
      // state, which would undo the value just picked — and the queued submit
      // would then collect that stale form. Drop it before it can land.
      FacetFiltersForm.abortInFlight();
      this.debouncedOnSubmit(event);
    });

    this.facetsWrapper?.addEventListener(
      "keydown",
      this.onHandleKeyDown.bind(this)
    );

    this.facetButtons.forEach((button) =>
      button.addEventListener("click", this.handleShowButtonsClick.bind(this))
    );

    this.closeButton?.addEventListener("click", this.closeFacets);
    this.facetsOverlay?.addEventListener("click", this.closeFacets);
    this.facetSubmitButton?.addEventListener("click", this.closeFacets);

    // The visible sort value mirrors the native select, so the field is as wide
    // as the selected option instead of the widest one.
    this.sortSelect = this.querySelector(".facets-sorting__select");
    this.sortValue = this.querySelector(".facets-sorting__value");

    this.sortSelect?.addEventListener("change", () => {
      const option = this.sortSelect.options[this.sortSelect.selectedIndex];
      if (this.sortValue && option) {
        this.sortValue.textContent = option.textContent.trim();
      }
    });

    if (this.classList.contains("facets--horizontal")) {
      this.initHorizontalDisclosures();
    }
  }

  disconnectedCallback() {
    this.clearFocusTrap();
    this.layoutMedia?.removeEventListener("change", this.keepDisclosuresOpen);
  }

  // In the horizontal sheet the groups ARE the columns, so they stay expanded:
  // the summary is inert in CSS, and a keyboard toggle is undone here. Below the
  // breakpoint the sheet falls back to the drawer and keeps its accordion.
  initHorizontalDisclosures() {
    this.layoutMedia = window.matchMedia(HORIZONTAL_LAYOUT_QUERY);
    this.keepDisclosuresOpen = () => {
      if (!this.layoutMedia.matches) return;

      this.querySelectorAll(".facets__disclosure").forEach((disclosure) => {
        disclosure.open = true;
      });
    };

    this.layoutMedia.addEventListener("change", this.keepDisclosuresOpen);
    // `toggle` doesn't bubble — catch it on the way down.
    this.addEventListener("toggle", this.keepDisclosuresOpen, true);
    this.keepDisclosuresOpen();
  }

  // A mouse has no horizontal wheel, so the quick filter row is draggable below
  // $lg where it scrolls. Delegated from the document on purpose: the row is
  // re-rendered on every filter change, and one page-level listener set never
  // needs re-binding or teardown.
  static initQuickFilterDrag() {
    const ROW = ".collection-quick-filters__items";
    let row = null;
    let startX = 0;
    let startScroll = 0;
    let dragged = false;

    const onPointerMove = (event) => {
      if (!row) return;

      const delta = event.clientX - startX;
      if (Math.abs(delta) > 3) dragged = true;
      row.scrollLeft = startScroll - delta;
    };

    const onPointerUp = () => {
      document.removeEventListener("pointermove", onPointerMove);
      row?.classList.remove("is-dragging");
      row = null;
      // The click lands right after this, so the flag is cleared behind it.
      setTimeout(() => {
        dragged = false;
      });
    };

    document.addEventListener("pointerdown", (event) => {
      // Touch and pen already pan the row natively.
      if (event.pointerType !== "mouse" || event.button !== 0) return;

      const target = event.target.closest(ROW);
      if (!target || target.scrollWidth <= target.clientWidth) return;

      row = target;
      startX = event.clientX;
      startScroll = target.scrollLeft;
      dragged = false;
      target.classList.add("is-dragging");

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp, { once: true });
    });

    // A drag must not toggle the tab it happens to end on.
    document.addEventListener(
      "click",
      (event) => {
        if (!dragged || !event.target.closest(ROW)) return;

        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // Tag tabs are links, and a link would otherwise start a native drag.
    document.addEventListener("dragstart", (event) => {
      if (event.target.closest(ROW)) event.preventDefault();
    });
  }

  // The row is re-rendered by the response (and a tag row navigates away), so a
  // clicked link takes its selected state up front — the pill fill needs a frame
  // of its own before that. Delegated for the same reason as the drag above.
  static initQuickFilterSelection() {
    document.addEventListener("click", (event) => {
      const link = event.target.closest("a.collection-quick-filters__item");
      const row = link?.closest(".collection-quick-filters__items");
      if (!row) return;

      // Both link flavours can clear the current selection: the product type
      // row's "All", and a tag tab that is already on — it points back at the
      // row's unfiltered URL, the same place the clear tab goes. Reading the
      // destination beats reading the classes, which a click that has not been
      // answered yet has already moved.
      const clear = row.querySelector(".collection-quick-filters__item--clear");
      const isClearing =
        clear && link.getAttribute("href") === clear.getAttribute("href");
      const next = isClearing ? clear : link;

      row
        .querySelectorAll(".collection-quick-filters__item.active")
        .forEach((item) => item.classList.remove("active"));
      // Checked boxes drive their own chip, and this link drops all of them.
      row
        .querySelectorAll('input[type="checkbox"]:checked')
        .forEach((input) => {
          input.checked = false;
        });

      next.classList.add("active");
    });

    // Checking a chip in the product type row clears "All" up front (and
    // unchecking the last one hands it back), so the row never shows two
    // selected chips while the products load.
    document.addEventListener("change", (event) => {
      const input = event.target;
      if (
        !input.matches?.(
          '.collection-quick-filters__items input[type="checkbox"]'
        )
      ) {
        return;
      }

      const row = input.closest(".collection-quick-filters__items");
      const clear = row.querySelector(".collection-quick-filters__item--clear");
      if (!clear) return;

      const hasSelection = Boolean(
        row.querySelector('input[type="checkbox"]:checked')
      );
      clear.classList.toggle("active", !hasSelection);
    });
  }

  // The fixed opener is pinned to the bottom of the viewport, where the footer would
  // otherwise scroll underneath it. An observer on the footer costs less than a scroll
  // handler, and the modifier takes the pill out of the tab order along with the view.
  static initFooterOverlapObserver() {
    FacetFiltersForm.teardownFooterOverlapObserver();

    FacetFiltersForm.footerElement = document.querySelector(".footer");
    if (!FacetFiltersForm.footerElement) {
      FacetFiltersForm.footerReached = false;
      FacetFiltersForm.syncFixedButtonVisibility();
      return;
    }

    FacetFiltersForm.footerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          FacetFiltersForm.footerReached = entry.isIntersecting;
          FacetFiltersForm.syncFixedButtonVisibility();
        });
      },
      { rootMargin: FOOTER_OVERLAP_MARGIN }
    );

    FacetFiltersForm.footerObserver.observe(FacetFiltersForm.footerElement);
    // An opener re-rendered by the editor starts from the state already measured.
    FacetFiltersForm.syncFixedButtonVisibility();
  }

  static teardownFooterOverlapObserver() {
    FacetFiltersForm.footerObserver?.disconnect();
    FacetFiltersForm.footerObserver = null;
    FacetFiltersForm.footerElement = null;
  }

  // A footer that sits inside the band even at the top of the page would hide the
  // opener everywhere and leave the sheet unreachable — a short collection has
  // nothing to scroll. The pill stays put in that case.
  static canHideForFooter() {
    const footer = FacetFiltersForm.footerElement;
    if (!footer) return false;

    const footerTop = footer.getBoundingClientRect().top + window.scrollY;

    return footerTop >= window.innerHeight - FOOTER_OVERLAP_OFFSET;
  }

  static syncFixedButtonVisibility() {
    const hidden =
      FacetFiltersForm.footerReached && FacetFiltersForm.canHideForFooter();

    document
      .querySelectorAll(".facets-button-show--fixed")
      .forEach((button) => {
        button.classList.toggle(FIXED_BUTTON_HIDDEN_CLASS, hidden);
      });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    document
      .getElementById("ProductGridContainer")
      ?.querySelector(".collection")
      ?.classList.add("loading");

    document.querySelectorAll(".facets__reset").forEach((btn) => {
      btn.classList.add("loading");
    });

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    FacetFiltersForm.syncQuickFilterSort();
  }

  // Tag quick filters are plain links carrying the sort they were rendered with,
  // and they live outside the re-rendered filter forms — resync them so a sort
  // picked after load survives the next tag click.
  static syncQuickFilterSort() {
    // Direct children only — the product type row wraps its "All" link in
    // `facet-remove` and is re-rendered from the response anyway.
    const links = document.querySelectorAll(
      ".collection-quick-filters__items > a.collection-quick-filters__item"
    );
    if (!links.length) return;

    const sortBy = new URLSearchParams(window.location.search).get("sort_by");

    links.forEach((link) => {
      const url = new URL(link.getAttribute("href"), window.location.origin);

      if (sortBy) {
        url.searchParams.set("sort_by", sortBy);
      } else {
        url.searchParams.delete("sort_by");
      }

      link.setAttribute("href", `${url.pathname}${url.search}`);
    });
  }

  // Filtering is last-pick-wins: only one request may be in flight, so an older
  // one can never render its server state over pills the shopper has moved past.
  static abortInFlight() {
    FacetFiltersForm.inFlight?.abort();
    FacetFiltersForm.inFlight = null;
  }

  // The section always ships the grid container, so a response without it is a
  // redirect or an error page — not markup to render from or to cache.
  static isSectionResponse(html) {
    return (
      typeof html === "string" && html.includes('id="ProductGridContainer"')
    );
  }

  static renderSectionFromFetch(url, event) {
    FacetFiltersForm.abortInFlight();
    const controller = new AbortController();
    FacetFiltersForm.inFlight = controller;

    fetch(url, { signal: controller.signal })
      .then((response) => (response.ok ? response.text() : ""))
      .then((html) => {
        FacetFiltersForm.inFlight = null;

        // Rendering half a page away is worse than leaving the current one up:
        // the filter rows would be emptied and the quick filter row dropped
        // entirely while the products they belong to stay on screen.
        if (!FacetFiltersForm.isSectionResponse(html)) {
          FacetFiltersForm.clearLoading();
          return;
        }

        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
      })
      .catch((error) => {
        // An abort means a newer request took over — it owns the loading state.
        if (error.name === "AbortError") return;
        FacetFiltersForm.clearLoading();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    FacetFiltersForm.abortInFlight();
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
  }

  // Nothing to render from means the response was not the section — an expired
  // storefront session, a redirect, a proxy error. Keep what is on screen and drop
  // the loading state instead of throwing halfway through the render.
  static clearLoading() {
    document
      .getElementById("ProductGridContainer")
      ?.querySelector(".collection")
      ?.classList.remove("loading");

    document.querySelectorAll(".facets__reset").forEach((btn) => {
      btn.classList.remove("loading");
    });
  }

  static renderProductGridContainer(html) {
    const source = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer");
    const target = document.getElementById("ProductGridContainer");

    if (!source || !target) {
      FacetFiltersForm.clearLoading();
      return;
    }

    target.innerHTML = source.innerHTML;

    if (
      document.querySelector(".js-load-more") ||
      document.querySelector(".js-infinite-scroll")
    ) {
      initLoadMore();
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    FacetFiltersForm.renderQuickFilters(parsedHTML);

    // Every filter form is re-rendered (modal groups + the top bar quick filters),
    // so the quick filter pills stay in sync with the current filter set.
    const facetDetailsElements = parsedHTML.querySelectorAll(
      "facet-filters-form form .js-filter"
    );
    // Only a modal group keeps its own DOM while the user interacts with it.
    const matchesIndex = (element) => {
      const jsFilter = event
        ? event.target.closest("#FacetFiltersForm .js-filter")
        : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );

    const sourceCurrentFilter =
      Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      );
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (sourceCurrentFilter) {
      FacetFiltersForm.updateCurrentFilter(
        sourceCurrentFilter,
        event.target.closest(".js-filter")
      );
    }

    document.querySelectorAll(".facets__reset").forEach((btn) => {
      btn.classList.remove("loading");
    });
  }

  // The quick filters block isn't rendered at all while it has nothing to show, so a
  // row that appears or disappears with the new filter set has to be swapped in or out
  // as a whole — the `.js-filter` sync only refills a row that is already there.
  static renderQuickFilters(html) {
    const source = html.querySelector(
      ".main-collection__top-block--quick-filters"
    );
    const target = document.querySelector(
      ".main-collection__top-block--quick-filters"
    );

    if (Boolean(source) === Boolean(target)) return;

    if (source) {
      document
        .querySelector(".main-collection__top-wrapper")
        ?.prepend(source.cloneNode(true));
    } else {
      target.remove();
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;

      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });
  }

  static renderProductCount(html) {
    const sourceCount = html.getElementById("ProductCount");
    const destinationCount = document.getElementById("ProductCount");
    if (sourceCount && destinationCount) {
      destinationCount.innerHTML = sourceCount.innerHTML;
    }
  }

  // The applied-filters count lives in two places: the Filter button badge and
  // the Apply Filters label. Both come from the server render.
  static renderAdditionalElements(html) {
    const sourceCount = html?.querySelector(".filters-count");

    if (sourceCount) {
      const isHidden = sourceCount.classList.contains("hidden");

      document.querySelectorAll(".filters-count").forEach((destination) => {
        destination.innerHTML = sourceCount.innerHTML;
        destination.classList.toggle("hidden", isHidden);
      });
    }

    const sourceSubmit = html?.querySelector(".facets__submit");
    const destinationSubmit = document.querySelector(".facets__submit");
    if (sourceSubmit && destinationSubmit) {
      destinationSubmit.innerHTML = sourceSubmit.innerHTML;

      const submitLabel = sourceSubmit.getAttribute("aria-label");
      if (submitLabel)
        destinationSubmit.setAttribute("aria-label", submitLabel);
    }
  }

  // The whole summary row is replaced so the Reset link appears and disappears
  // with the group's active values.
  static updateCurrentFilter(source, target) {
    const sourceSummary = source.querySelector(".facets__summary-inner");
    const targetSummary = target.querySelector(".facets__summary-inner");
    if (sourceSummary && targetSummary) {
      targetSummary.innerHTML = sourceSummary.innerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  // Collects every filter form (the modal `FacetFiltersForm` and the top bar
  // quick filters `FacetFiltersForm<Name>`) plus the sort form. The form that
  // fired the event OWNS every parameter name it can submit, so a value just
  // unchecked there is never resurrected from another form that still holds it.
  onSubmitHandler(event) {
    event.preventDefault();

    const originForm = event.target.closest("form");
    const ownedKeys = new Set();
    originForm
      ?.querySelectorAll("[name]")
      .forEach((input) => ownedKeys.add(input.name));

    const searchParams = new URLSearchParams();
    const addedParams = new Set();

    document.querySelectorAll("facet-filters-form form").forEach((form) => {
      const isFilterForm = form.id.indexOf("FacetFiltersForm") === 0;
      if (!isFilterForm && form.id !== "FacetSortForm") return;

      new FormData(form).forEach((value, key) => {
        if (form !== originForm && ownedKeys.has(key)) return;

        const param = `${key}=${value}`;
        if (addedParams.has(param)) return;
        addedParams.add(param);
        searchParams.append(key, value);
      });
    });

    this.onSubmitForm(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }

  handleFacetButtonClick(event) {
    event.preventDefault();

    if (!this.facetButtons.length || !this.facetForm || !this.facetsWrapper) {
      return;
    }

    this.facetsWrapper.classList.add("active");
    this.setButtonsExpanded(true);

    // The click can land on a span inside the opener, and the close hands this
    // straight to focus() — resolve it to the button itself.
    this.focusElement =
      event.target.closest?.(".facets-button-show") || document.activeElement;

    document.body.classList.add("overflow-hidden");

    this.armFocusTrap();
  }

  // Focus moves in once the sheet has finished opening. A transition that never lands
  // (motion turned off, an open the user closes again mid-way) must not leave the
  // dialog untrapped, so the timer backs it up and the first one in cancels the other.
  armFocusTrap() {
    this.clearFocusTrap();

    const dialog = this.querySelector('[role="dialog"]');
    if (!dialog) return;

    const trap = () => {
      this.clearFocusTrap();
      if (!this.facetsWrapper?.classList.contains("active")) return;

      trapFocus(this, dialog);
    };

    this.focusTrapController = new AbortController();
    this.addEventListener("transitionend", trap, {
      signal: this.focusTrapController.signal,
    });
    this.focusTrapTimer = setTimeout(trap, FOCUS_TRAP_FALLBACK_MS);
  }

  clearFocusTrap() {
    if (this.focusTrapTimer) {
      clearTimeout(this.focusTrapTimer);
      this.focusTrapTimer = null;
    }

    this.focusTrapController?.abort();
    this.focusTrapController = null;
  }

  closeFacets(event) {
    event.preventDefault();

    if (!this.facetsWrapper) return;

    this.clearFocusTrap();
    this.facetsWrapper.classList.remove("active");
    this.setButtonsExpanded(false);
    document.body.classList.remove("overflow-hidden");

    this.returnFocus(this.focusElement);
  }

  // focus() on a hidden element is a silent no-op that strands focus on <body>, and
  // the fixed opener is hidden twice over: it still computes `visibility: hidden` in
  // the task the open state is dropped (its transition is only at progress 0), and it
  // may have reached the footer while the sheet was up. So the focus is retried once
  // the transition has moved on, and the main landmark catches the pill that stays gone.
  returnFocus(target) {
    if (!target) {
      removeTrapFocus();
      return;
    }

    removeTrapFocus(target);
    if (document.activeElement === target) return;

    requestAnimationFrame(() => {
      if (document.body.contains(target)) target.focus();
      if (document.activeElement === target) return;

      document.getElementById("MainContent")?.focus({ preventScroll: true });
    });
  }

  // The opener doubles as the close control (the fixed variant swaps its icon on
  // the open state), so every button mirrors the modal state.
  setButtonsExpanded(isExpanded) {
    this.facetButtons.forEach((button) =>
      button.setAttribute("aria-expanded", isExpanded ? "true" : "false")
    );
  }

  onHandleKeyDown(event) {
    if (
      event.key === "Escape" &&
      this.facetsWrapper?.classList.contains("active")
    ) {
      this.closeFacets(event);
    }
  }

  handleShowButtonsClick(event) {
    // Every facet-filters-form on the page binds the same openers, so the first
    // handler owns the click — otherwise the toggle would open and close at once.
    if (event.defaultPrevented) return;
    event.preventDefault();

    // targetId - FacetFiltersForm or FacetSortForm
    const targetId =
      event.currentTarget.dataset.desktopModal ||
      event.currentTarget.dataset.mobileModal;

    const targetForm = document.querySelector(
      `[data-filters-form="${targetId}"]`
    );
    if (!targetForm) return;

    const isOpen = targetForm
      .querySelector(".facets__wrapper")
      ?.classList.contains("active");

    if (isOpen) {
      targetForm.closeFacets(event);
      return;
    }

    targetForm.handleFacetButtonClick(event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.inFlight = null;
FacetFiltersForm.footerObserver = null;
FacetFiltersForm.footerElement = null;
FacetFiltersForm.footerReached = false;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.initQuickFilterDrag();
FacetFiltersForm.initQuickFilterSelection();
FacetFiltersForm.initFooterOverlapObserver();

// The editor re-renders the footer and swaps the opener between its standard and
// fixed positions, so the observer is rebuilt on every section load — the re-init
// disconnects the previous one first, and the sync repaints whatever opener is there.
if (!window.__themeFacetsFooterOverlapEvents) {
  window.__themeFacetsFooterOverlapEvents = true;

  document.addEventListener("shopify:section:load", () => {
    FacetFiltersForm.initFooterOverlapObserver();
  });

  document.addEventListener("shopify:section:unload", (event) => {
    if (!event.target.contains(FacetFiltersForm.footerElement)) return;

    FacetFiltersForm.teardownFooterOverlapObserver();
  });
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
    this.controlSlider();
    this.controlInput();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll(".field__input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") {
      maxInput.setAttribute("min", 0);
    }
    if (maxInput.value === "") {
      minInput.setAttribute("max", maxInput.getAttribute("max"));
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  fillSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    inputRangeWrappers.forEach((inputWrapper) => {
      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];

      const range = Number(inputRangeTo.max) - Number(inputRangeTo.min);
      const fromCurrent =
        Number(inputRangeFrom.value) - Number(inputRangeTo.min);
      const toCurrent = Number(inputRangeTo.value) - Number(inputRangeTo.min);
      const minRange = (fromCurrent / range) * 100;
      const maxRange = (toCurrent / range) * 100;

      inputWrapper.setAttribute(
        "style",
        `--range-min: ${minRange}%; --range-max: ${maxRange}%`
      );
    });
  }

  controlSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    const inputNumberWrappers = document.querySelectorAll(
      ".facets__price .facets__price-wrapper"
    );

    inputRangeWrappers.forEach((inputWrapper, index) => {
      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      const inputNumberFrom =
        inputNumberWrappers[index].querySelectorAll(".field__input")[0];
      const inputNumberTo =
        inputNumberWrappers[index].querySelectorAll(".field__input")[1];

      inputRangeFrom.oninput = () => {
        const from = Number(inputRangeFrom.value);
        const to = Number(inputRangeTo.value);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputNumberFrom.value = from;
        }

        this.fillSlider();
      };

      if (Number(inputRangeTo.value) <= 0) {
        inputRangeTo.style.zIndex = 2;
      } else {
        inputRangeTo.style.zIndex = 0;
      }

      inputRangeTo.oninput = () => {
        const from = Number(inputRangeFrom.value);
        const to = Number(inputRangeTo.value);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        } else {
          inputNumberTo.value = from;
          inputRangeTo.value = from;
        }

        if (Number(inputRangeTo.value) <= 0) {
          inputRangeTo.style.zIndex = 2;
        } else {
          inputRangeTo.style.zIndex = 0;
        }

        this.fillSlider();
      };
    });
  }

  controlInput() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    const inputNumberWrappers = document.querySelectorAll(
      ".facets__price .facets__price-wrapper"
    );

    inputNumberWrappers.forEach((inputWrapper, index) => {
      const inputsNumber = inputWrapper.querySelectorAll(".field__input");
      const inputNumberFrom = inputsNumber[0];
      const inputNumberTo = inputsNumber[1];
      const inputRangeFrom =
        inputRangeWrappers[index].querySelectorAll(".field__range")[0];
      const inputRangeTo =
        inputRangeWrappers[index].querySelectorAll(".field__range")[1];

      inputNumberFrom.oninput = () => {
        const from = Number(inputNumberFrom.value);
        const to = Number(inputNumberTo.value);

        if (to && from > to) {
          inputRangeFrom.value = to;
        } else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      };

      inputNumberTo.oninput = () => {
        const from = Number(inputNumberFrom.value);
        const to = Number(inputNumberTo.value);

        if (to >= from) {
          inputRangeTo.value = to;
        } else {
          inputRangeTo.value = from;
        }

        this.fillSlider();
      };

      inputNumberFrom.onblur = () => {
        if (inputNumberFrom.value === "") return;
        const from = Number(inputNumberFrom.value);
        const min = Number(inputNumberFrom.getAttribute("min"));
        const max = Number(inputNumberFrom.getAttribute("max"));

        if (from < min) {
          inputRangeFrom.value = min;
          inputNumberFrom.value = min;
        } else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      };

      inputNumberTo.onblur = () => {
        if (inputNumberTo.value === "") return;
        const to = Number(inputNumberTo.value);
        const min = Number(inputNumberTo.getAttribute("min"));
        const max = Number(inputNumberTo.getAttribute("max"));

        if (to > max) {
          inputRangeTo.value = max;
          inputNumberTo.value = max;
        } else {
          inputRangeTo.value = to;
        }

        this.fillSlider();
      };
    });
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
    form.render;
  }
}

customElements.define("facet-remove", FacetRemove);
